/**
 * Bundle 42 - Broken media report page. Super-admin only.
 * Every URL the hydrator or rollback op skipped because HEAD returned
 * 401/403/404, deduped by (url, source) with hit_count + last_seen_at.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle } from 'lucide-react';
import api from '@/utils/api';

const AdminBrokenMediaReportPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ok = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/admin/maintenance/broken-media-report?limit=200');
        if (ok) setRows(data.rows || []);
      } catch {
        if (ok) setRows([]);
      } finally { if (ok) setLoading(false); }
    })();
    return () => { ok = false; };
  }, []);

  return (
    <div className="p-6 max-w-6xl space-y-6" data-testid="admin-broken-media-report-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-primary-900 flex items-center gap-2">
          <AlertTriangle className="h-6 w-6" /> Broken media report
        </h1>
        <p className="text-sm text-neutral-600 mt-1">
          Every media URL the hydrator or the photo rollback op has skipped
          because HEAD returned 401/403/404. Rows dedup by (url, source).
        </p>
      </div>

      <Card className="border shadow-soft">
        <CardHeader className="pb-3"><CardTitle className="text-sm">Skipped URLs</CardTitle></CardHeader>
        <CardContent>
          {loading && <div className="text-xs text-neutral-500">Loading...</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-neutral-500 py-8 text-center border border-dashed border-neutral-200 rounded-xl">
              No broken media recorded. All hydrator writes have been passing HEAD checks.
            </div>
          )}
          {!loading && rows.length > 0 && (
            <table className="w-full text-xs" data-testid="broken-media-table">
              <thead>
                <tr className="text-left text-neutral-500 border-b border-neutral-200">
                  <th className="py-2 pr-3">URL</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Reason</th>
                  <th className="py-2 pr-3">Hits</th>
                  <th className="py-2 pr-3">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-neutral-100 last:border-0">
                    <td className="py-1 pr-3 font-mono truncate max-w-[380px]" title={r.url}>{r.url}</td>
                    <td className="py-1 pr-3">{r.source}</td>
                    <td className="py-1 pr-3 font-mono">{r.last_status ?? '-'}</td>
                    <td className="py-1 pr-3">{r.last_reason}</td>
                    <td className="py-1 pr-3 font-mono">{r.hit_count}</td>
                    <td className="py-1 pr-3 font-mono">{r.last_seen_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBrokenMediaReportPage;
